// @flow

import * as React from 'react';
import {useContext, useReducer} from 'react';
import {contextStubFunction} from 'docs/util';
import {reducer, initialState} from './reducer';

import type {
  State,
  Action,
  SetResourceAction,
  SetActionAction,
  SetParamsAction,
  SetPathArgsAction,
  SetCodegenExampleAction,
  SetApiResponseAction,
} from './reducer';

type Dispatch = (Action) => void;

const setResource = (dispatch: Dispatch) => (
  payload: $PropertyType<SetResourceAction, 'payload'>,
) => {
  dispatch({type: 'SET_RESOURCE', payload});
};

const setAction = (dispatch: Dispatch) => (
  payload: $PropertyType<SetActionAction, 'payload'>,
) => {
  dispatch({type: 'SET_ACTION', payload});
};

const setParams = (dispatch: Dispatch) => (
  payload: $PropertyType<SetParamsAction, 'payload'>,
) => {
  dispatch({type: 'SET_PARAMS', payload});
};

const setPathArgs = (dispatch: Dispatch) => (
  payload: $PropertyType<SetPathArgsAction, 'payload'>,
) => {
  dispatch({type: 'SET_PATH_ARGS', payload});
};

const setCodegenExample = (dispatch: Dispatch) => (
  payload: $PropertyType<SetCodegenExampleAction, 'payload'>,
) => {
  dispatch({type: 'SET_CODEGEN_EXAMPLE', payload});
};

const setApiResponse = (dispatch: Dispatch) => (
  payload: $PropertyType<SetApiResponseAction, 'payload'>,
) => {
  dispatch({type: 'SET_API_RESPONSE', payload});
};

type ApiExplorerContextType = {
  state: State,
  setResource: ($PropertyType<SetResourceAction, 'payload'>) => void,
  setAction: ($PropertyType<SetActionAction, 'payload'>) => void,
  setParams: ($PropertyType<SetParamsAction, 'payload'>) => void,
  setPathArgs: ($PropertyType<SetPathArgsAction, 'payload'>) => void,
  setCodegenExample: (
    $PropertyType<SetCodegenExampleAction, 'payload'>,
  ) => void,
  setApiResponse: ($PropertyType<SetApiResponseAction, 'payload'>) => void,
};

export const ApiExplorerContext = React.createContext<ApiExplorerContextType>({
  state: initialState,
  setResource: contextStubFunction('ApiExplorer'),
  setAction: contextStubFunction('ApiExplorer'),
  setParams: contextStubFunction('ApiExplorer'),
  setPathArgs: contextStubFunction('ApiExplorer'),
  setCodegenExample: contextStubFunction('ApiExplorer'),
  setApiResponse: contextStubFunction('ApiExplorer'),
});

type ApiExplorerProviderProps = {
  children?: React.Node,
};

export const ApiExplorerProvider = (props: ApiExplorerProviderProps) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const context = {
    state,
    setResource: setResource(dispatch),
    setAction: setAction(dispatch),
    setParams: setParams(dispatch),
    setPathArgs: setPathArgs(dispatch),
    setCodegenExample: setCodegenExample(dispatch),
    setApiResponse: setApiResponse(dispatch),
  };

  return (
    <ApiExplorerContext.Provider value={context}>
      {props.children}
    </ApiExplorerContext.Provider>
  );
};

export const useApiExplorer = () => {
  return useContext(ApiExplorerContext);
};

export default ApiExplorerProvider;
